import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, ShieldCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const INTENT_LABELS = {
  foundation: "Foundation (Family Wallet + Anna reactive AI)",
  engaged: "Engaged (Anna proactive + counsellor debrief)",
  premium: "Premium (dedicated counsellor, 20 sessions)",
  attestation: "Grade attestation (official cross-curriculum equivalency letter)",
};

export default function WuaPlusBook() {
  const [params] = useSearchParams();
  const intent = params.get("intent");
  const targetGrade = params.get("target_grade");
  const intendedSubjectQP = params.get("intended_subject");
  const source = params.get("source");
  const intentLabel = intent && INTENT_LABELS[intent] ? INTENT_LABELS[intent] : null;

  // Pre-populate the notes with any context the referring surface passed
  // in — Quick Check's escape-hatch, for example, pipes the target grade
  // and intended subject through so the counsellor has a running start.
  const initialNotes = (() => {
    const parts = [];
    if (intentLabel) parts.push(`[Interested in: ${intentLabel}]`);
    if (targetGrade) parts.push(`[Target grade: ${targetGrade}]`);
    if (source === "quick-check-escape-hatch") {
      parts.push("[From Quick Check — grade profile currently a stretch. Would like a 1:1 tutor bolt-on.]");
    }
    return parts.length ? parts.join(" ") + " " : "";
  })();

  const [form, setForm] = useState({
    name: "", email: "", country: "", phone: "",
    intended_subject: intendedSubjectQP || "",
    preferred_time: "",
    notes: initialNotes,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // {inquiry_id}

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/wua-plus/book", { ...form, intent });
      setConfirmed(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-16 text-center"
           data-testid="wua-plus-confirmation">
        <CheckCircle2 className="w-14 h-14 text-emerald-700 mx-auto" />
        <h1 className="font-editorial text-3xl mt-4">Booking request received.</h1>
        <p className="mt-3 text-stone-700">
          Reference <span className="font-mono">{confirmed.inquiry_id}</span>. A WUA+ advisor will reach out to <span className="font-semibold">{form.email}</span> within 2 working days to schedule your session.
        </p>
        <p className="mt-2 text-xs text-stone-500">
          Keep the reference handy in case you email us in the meantime.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <img
        src="/logo-plus.png"
        alt="WhichUniversityAdvisor+"
        data-testid="wua-plus-book-logo"
        className="h-14 sm:h-16 w-auto mb-5"
      />
      <div className="label-uppercase text-stone-500">WUA+ consultation</div>
      <h1 className="font-editorial text-4xl sm:text-5xl mt-2 leading-tight">
        Book a 1-to-1 session with our editorial team.
      </h1>
      <p className="mt-4 text-stone-700 max-w-2xl">
        60 minutes on video with a senior WUA advisor — we walk through your shortlist, admissions strategy, financial plan, and visa route. No sales pitch, no commissions.
      </p>

      {intentLabel && (
        <div
          data-testid="wua-plus-intent-banner"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] px-3 py-1.5 text-[11px] font-underground-sc tracking-wider text-[#4C1D95]"
        >
          <ShieldCheck className="w-3 h-3" />
          Enquiring about: {intentLabel}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3 text-xs">
        <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
          <span className="font-underground-sc tracking-wider">No commissions — editorial neutrality</span>
        </div>
        <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
          <MessageCircle className="w-3.5 h-3.5 text-stone-600" />
          <span className="font-underground-sc tracking-wider">Response within 2 working days</span>
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 grid gap-5" data-testid="wua-plus-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="name">Your name *</Label>
            <Input id="name" required data-testid="wua-plus-name" value={form.name} onChange={update("name")} />
          </div>
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input id="email" type="email" required data-testid="wua-plus-email" value={form.email} onChange={update("email")} />
          </div>
          <div>
            <Label htmlFor="country">Country of residence</Label>
            <Input id="country" data-testid="wua-plus-country" value={form.country} onChange={update("country")} placeholder="UAE, India, UK…" />
          </div>
          <div>
            <Label htmlFor="phone">Phone (optional, incl. code)</Label>
            <Input id="phone" data-testid="wua-plus-phone" value={form.phone} onChange={update("phone")} placeholder="+971…" />
          </div>
          <div>
            <Label htmlFor="intended_subject">Intended subject</Label>
            <Input id="intended_subject" data-testid="wua-plus-subject" value={form.intended_subject} onChange={update("intended_subject")} placeholder="Computer Science" />
          </div>
          <div>
            <Label htmlFor="preferred_time">Preferred time to talk</Label>
            <Input id="preferred_time" data-testid="wua-plus-time" value={form.preferred_time} onChange={update("preferred_time")} placeholder="Weekend afternoon (GMT)" />
          </div>
        </div>
        <div>
          <Label htmlFor="notes">What would you like us to focus on?</Label>
          <Textarea
            id="notes" rows={5}
            data-testid="wua-plus-notes"
            value={form.notes} onChange={update("notes")}
            placeholder="Country choices, budget constraints, scholarship strategy, application timeline…"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="wua-plus-submit"
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white px-6 py-2.5"
          >
            {submitting ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</>)
                        : "Request my session"}
          </Button>
          <span className="text-xs text-stone-500">We reply within 2 working days.</span>
        </div>
      </form>
    </div>
  );
}
